import { useEffect, useMemo, useState } from "react";
import type { Work } from "@/types/gallery";
import WorkCard from "@/components/base/WorkCard";
import Pagination from "@/components/base/Pagination";
import FilterSidebar from "@/components/feature/FilterSidebar";
import {
  emptyFilters,
  filterWorks,
  getFilterOptions,
  paginate,
  type WorkFilters,
} from "@/utils/gallery";

interface WorkExplorerProps {
  baseWorks: Work[];
  pageSize?: number;
  resetKey?: string;
}

export default function WorkExplorer({ baseWorks, pageSize = 8, resetKey = "" }: WorkExplorerProps) {
  const [filters, setFilters] = useState<WorkFilters>(emptyFilters);
  const [page, setPage] = useState(1);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const options = useMemo(() => getFilterOptions(baseWorks), [baseWorks]);

  const filtered = useMemo(() => filterWorks(baseWorks, filters), [baseWorks, filters]);
  const paged = useMemo(() => paginate(filtered, page, pageSize), [filtered, page, pageSize]);

  useEffect(() => {
    setFilters(emptyFilters);
    setPage(1);
  }, [resetKey]);

  const toggleFilter = (key: keyof WorkFilters, value: string) => {
    setFilters((prev) => {
      const current = prev[key];
      const next = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      return { ...prev, [key]: next };
    });
    setPage(1);
  };

  const resetFilters = () => {
    setFilters(emptyFilters);
    setPage(1);
  };

  const activeCount =
    filters.styles.length + filters.scenes.length + filters.models.length + filters.years.length;

  const groups = [
    { key: "styles" as const, label: "拍摄风格", options: options.styles },
    { key: "scenes" as const, label: "拍摄场景", options: options.scenes },
    { key: "models" as const, label: "模特姓名", options: options.models },
    { key: "years" as const, label: "拍摄年份", options: options.years },
  ];

  const handlePageChange = (next: number) => {
    setPage(next);
    window.scrollTo({ top: 200, behavior: "smooth" });
  };

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-[260px_1fr]">
      <aside className="hidden lg:block">
        <div className="sticky top-24">
          <FilterSidebar
            groups={groups}
            filters={filters}
            onToggle={toggleFilter}
            onReset={resetFilters}
            activeCount={activeCount}
          />
        </div>
      </aside>

      <div>
        <div className="mb-5 flex items-center justify-between">
          <p className="text-sm text-foreground-600">
            共 <span className="font-semibold text-foreground-950">{filtered.length}</span> 组作品
            {activeCount > 0 && <span className="ml-2 text-primary-600">已筛选 {activeCount} 项</span>}
          </p>
          <button
            type="button"
            onClick={() => setMobileFilterOpen(true)}
            className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 lg:hidden"
          >
            <i className="ri-filter-3-line" />
            筛选{activeCount > 0 ? `（${activeCount}）` : ""}
          </button>
        </div>

        {paged.items.length > 0 ? (
          <>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {paged.items.map((work, index) => (
                <WorkCard key={work.id} work={work} index={index} />
              ))}
            </div>
            <Pagination currentPage={paged.page} totalPages={paged.totalPages} onChange={handlePageChange} />
          </>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-inbox-archive-line text-3xl" />
            </div>
            <h3 className="mt-5 text-base font-semibold text-foreground-950">暂无符合条件的作品</h3>
            <p className="mt-2 max-w-sm text-sm text-foreground-500">
              当前筛选条件下没有找到作品，试试调整筛选条件或重置后再浏览。
            </p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-6 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line" />
              重置筛选条件
            </button>
          </div>
        )}
      </div>

      {mobileFilterOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setMobileFilterOpen(false)}
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-background-50 p-5">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-semibold text-foreground-950">筛选条件</h2>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setMobileFilterOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-background-100 text-foreground-700"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <FilterSidebar
              groups={groups}
              filters={filters}
              onToggle={toggleFilter}
              onReset={resetFilters}
              activeCount={activeCount}
            />
            <button
              type="button"
              onClick={() => setMobileFilterOpen(false)}
              className="mt-5 w-full whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50"
            >
              查看 {filtered.length} 组作品
            </button>
          </div>
        </div>
      )}
    </div>
  );
}